import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, AlertTriangle, ClipboardCheck, Ban, FlaskConical, Stethoscope } from "lucide-react";
import VoiceInput from "@/components/VoiceInput";

/* ── Medications tab ────────────────────────────────────────────────── */
export function IPDMedications({ ipdId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ drug: "", dose: "", route: "PO", frequency: "BD", duration: "", indication: "", source: "inpatient", notes: "" });
  const load = () => api.get(`/ipd/${ipdId}/medications`).then((r) => setRows(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ipdId]);

  const add = async () => {
    if (!form.drug.trim()) return toast.error("Drug name required");
    try {
      const { data } = await api.post(`/ipd/${ipdId}/medications`, form);
      if (data.allergy_warning) toast.warning(`⚠ ${data.allergy_warning} — please review before administering.`);
      else toast.success("Medication added");
      setForm({ ...form, drug: "", dose: "", duration: "", indication: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const stop = async (m) => { await api.post(`/ipd/${ipdId}/medications/${m.id}/stop`); toast.success(`Stopped ${m.drug}`); load(); };
  const reconcile = async (m) => { await api.post(`/ipd/${ipdId}/medications/${m.id}/reconcile`); toast.success("Reconciled"); load(); };

  return (
    <div className="bg-white border p-4 space-y-3">
      <div className="grid grid-cols-6 gap-2">
        <VoiceInput as="input" placeholder="Drug (e.g. Ceftriaxone)" value={form.drug} onChange={(v) => setForm({ ...form, drug: v })} testid="ipd-med-drug" className="col-span-2"/>
        <Input placeholder="Dose (500mg)" value={form.dose} onChange={(e) => setForm({ ...form, dose: e.target.value })} className="h-9" data-testid="ipd-med-dose"/>
        <select value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })} className="border h-9 text-sm px-2">
          {["PO","IV","IM","SC","TOP","INH","PR","SL"].map((r) => <option key={r}>{r}</option>)}
        </select>
        <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="border h-9 text-sm px-2">
          {["OD","BD","TDS","QID","Q4H","Q6H","SOS","STAT","HS"].map((f) => <option key={f}>{f}</option>)}
        </select>
        <Input placeholder="Duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="h-9"/>
      </div>
      <div className="grid grid-cols-6 gap-2">
        <VoiceInput as="input" placeholder="Indication" value={form.indication} onChange={(v) => setForm({ ...form, indication: v })} className="col-span-2"/>
        <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="border h-9 text-sm px-2">
          <option value="inpatient">Inpatient</option>
          <option value="home">Home Medication</option>
          <option value="handover">Handover</option>
        </select>
        <VoiceInput as="input" placeholder="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} className="col-span-2"/>
        <Button onClick={add} className="bg-blue-700 hover:bg-blue-800" data-testid="ipd-med-add"><Plus className="w-4 h-4 mr-1"/> Add</Button>
      </div>
      <div className="text-[11px] text-slate-500">Every drug is auto-checked against recorded allergies (from Clinical → Allergies) and flags a warning if it matches.</div>
      <div className="border-t pt-2">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left"><tr><th className="p-2 label-tiny">Drug</th><th className="label-tiny">Dose</th><th className="label-tiny">Route</th><th className="label-tiny">Freq</th><th className="label-tiny">Duration</th><th className="label-tiny">Source</th><th className="label-tiny">Recon</th><th className="label-tiny w-40">Actions</th></tr></thead>
          <tbody>
            {rows.map((m) => {
              const stopped = !!m.stopped_at;
              return (
                <tr key={m.id} className={`border-b ${stopped ? "opacity-50" : ""}`} data-testid={`ipd-med-row-${m.id}`}>
                  <td className="p-2 font-medium">
                    {m.drug}
                    {m.allergy_warning && !stopped && <span className="ml-2 inline-flex items-center gap-1 text-red-700 text-[10px] bg-red-50 border border-red-200 rounded-sm px-1.5"><AlertTriangle className="w-3 h-3"/> {m.allergy_warning}</span>}
                    {m.notes && <div className="text-[10px] text-slate-500">{m.notes}</div>}
                  </td>
                  <td>{m.dose}</td><td>{m.route}</td><td>{m.frequency}</td><td>{m.duration}</td>
                  <td className="text-xs">{m.source}</td>
                  <td>{m.reconciled ? <Badge className="bg-teal-100 text-teal-800 rounded-sm">Reconciled</Badge> : <span className="text-[11px] text-slate-400">—</span>}</td>
                  <td className="text-xs">
                    {!stopped && (
                      <div className="flex gap-1">
                        {!m.reconciled && <button onClick={() => reconcile(m)} className="px-2 py-1 border border-teal-300 text-teal-700 hover:bg-teal-50 rounded-sm text-[11px]"><ClipboardCheck className="w-3 h-3 inline mr-1"/>Reconcile</button>}
                        <button onClick={() => stop(m)} className="px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm text-[11px]"><Ban className="w-3 h-3 inline mr-1"/>Stop</button>
                      </div>
                    )}
                    {stopped && <span className="text-[10px] text-slate-400">Stopped</span>}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-xs text-slate-500">No medications yet — add the first one above.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Investigations tab ─────────────────────────────────────────────── */
export function IPDInvestigations({ ipdId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ test: "", category: "Lab", indication: "", priority: "Routine" });
  const [resultDraft, setResultDraft] = useState({});
  const load = () => api.get(`/ipd/${ipdId}/investigations`).then((r) => setRows(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ipdId]);
  const order = async () => {
    if (!form.test.trim()) return toast.error("Test name required");
    await api.post(`/ipd/${ipdId}/investigations`, form);
    setForm({ ...form, test: "", indication: "" });
    toast.success("Ordered"); load();
  };
  const setStatus = async (inv, status) => {
    await api.put(`/ipd/${ipdId}/investigations/${inv.id}`, { status, result_summary: resultDraft[inv.id] || "" });
    toast.success(status.charAt(0).toUpperCase() + status.slice(1));
    setResultDraft((s) => ({ ...s, [inv.id]: "" }));
    load();
  };
  const statusColor = {
    ordered: "bg-amber-100 text-amber-800",
    collected: "bg-blue-100 text-blue-800",
    reported: "bg-teal-100 text-teal-800",
    cancelled: "bg-slate-200 text-slate-500 line-through",
  };
  return (
    <div className="bg-white border p-4 space-y-3">
      <div className="grid grid-cols-6 gap-2">
        <VoiceInput as="input" placeholder="Test (e.g. CBC / X-Ray Chest)" value={form.test} onChange={(v) => setForm({ ...form, test: v })} testid="ipd-inv-test" className="col-span-2"/>
        <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border h-9 text-sm px-2">
          {["Lab","Radiology","Cardiology","Other"].map((c) => <option key={c}>{c}</option>)}
        </select>
        <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="border h-9 text-sm px-2">
          {["Routine","Urgent","STAT"].map((p) => <option key={p}>{p}</option>)}
        </select>
        <VoiceInput as="input" placeholder="Indication" value={form.indication} onChange={(v) => setForm({ ...form, indication: v })}/>
        <Button onClick={order} className="bg-blue-700 hover:bg-blue-800" data-testid="ipd-inv-order"><Plus className="w-4 h-4 mr-1"/> Order</Button>
      </div>
      <div className="border-t pt-2 space-y-2">
        {rows.map((i) => (
          <div key={i.id} className="border rounded-sm p-2" data-testid={`ipd-inv-row-${i.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">
                  {i.test} <span className="text-[10px] text-slate-500">· {i.category} · {i.priority}</span>
                </div>
                <div className="text-[11px] text-slate-500">
                  {i.indication && `${i.indication} · `}
                  Ordered {new Date(i.ordered_at).toLocaleString("en-IN")}
                  {i.collected_at && ` · Collected ${new Date(i.collected_at).toLocaleString("en-IN")}`}
                  {i.reported_at && ` · Reported ${new Date(i.reported_at).toLocaleString("en-IN")}`}
                </div>
                {i.result_summary && <div className="text-xs bg-teal-50 border border-teal-200 rounded-sm px-2 py-1 mt-1">{i.result_summary}</div>}
              </div>
              <div className="flex flex-col items-end gap-1">
                <Badge className={`${statusColor[i.status]} rounded-sm`}>{i.status}</Badge>
                {i.status === "ordered" && (
                  <div className="flex gap-1">
                    <button onClick={() => setStatus(i, "collected")} className="px-2 py-1 border border-blue-300 text-blue-700 hover:bg-blue-50 rounded-sm text-[11px]">Collected</button>
                    <button onClick={() => setStatus(i, "cancelled")} className="px-2 py-1 border border-red-300 text-red-700 hover:bg-red-50 rounded-sm text-[11px]">Cancel</button>
                  </div>
                )}
                {i.status === "collected" && (
                  <div className="flex flex-col gap-1 items-end">
                    <Input value={resultDraft[i.id] || ""} onChange={(e) => setResultDraft((s) => ({ ...s, [i.id]: e.target.value }))} placeholder="Result summary" className="h-8 w-64 text-xs"/>
                    <button onClick={() => setStatus(i, "reported")} className="px-2 py-1 border border-teal-300 text-teal-700 hover:bg-teal-50 rounded-sm text-[11px]">Mark Reported</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="p-6 text-center text-xs text-slate-500">No investigations ordered yet.</div>}
      </div>
    </div>
  );
}

/* ── Procedures tab ─────────────────────────────────────────────────── */
export function IPDProcedures({ ipdId }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ procedure: "", indication: "", surgeon: "", anaesthetist: "", anaesthesia_type: "GA", started_at: "", finished_at: "", findings: "", steps: "", complications: "", post_op_orders: "", consent_taken: true });
  const load = () => api.get(`/ipd/${ipdId}/procedures`).then((r) => setRows(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ipdId]);

  const add = async () => {
    if (!form.procedure.trim()) return toast.error("Procedure name required");
    await api.post(`/ipd/${ipdId}/procedures`, form);
    toast.success("Procedure recorded");
    setForm({ ...form, procedure: "", indication: "", findings: "", steps: "", complications: "", post_op_orders: "" });
    load();
  };
  return (
    <div className="bg-white border p-4 space-y-3">
      <div className="grid grid-cols-6 gap-2">
        <VoiceInput as="input" placeholder="Procedure (e.g. Appendectomy)" value={form.procedure} onChange={(v) => setForm({ ...form, procedure: v })} className="col-span-2" testid="ipd-proc-name"/>
        <Input value={form.surgeon} onChange={(e) => setForm({ ...form, surgeon: e.target.value })} placeholder="Surgeon" className="h-9"/>
        <Input value={form.anaesthetist} onChange={(e) => setForm({ ...form, anaesthetist: e.target.value })} placeholder="Anaesthetist" className="h-9"/>
        <select value={form.anaesthesia_type} onChange={(e) => setForm({ ...form, anaesthesia_type: e.target.value })} className="border h-9 text-sm px-2">
          {["GA","SA","LA","Sedation","Regional"].map((a) => <option key={a}>{a}</option>)}
        </select>
        <Button onClick={add} className="bg-blue-700 hover:bg-blue-800" data-testid="ipd-proc-add"><Plus className="w-4 h-4 mr-1"/> Add</Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="label-tiny">Start</Label><Input type="datetime-local" value={form.started_at} onChange={(e) => setForm({ ...form, started_at: e.target.value })} className="h-9"/></div>
        <div><Label className="label-tiny">Finish</Label><Input type="datetime-local" value={form.finished_at} onChange={(e) => setForm({ ...form, finished_at: e.target.value })} className="h-9"/></div>
      </div>
      <VoiceInput placeholder="Indication" value={form.indication} onChange={(v) => setForm({ ...form, indication: v })} rows={2}/>
      <VoiceInput placeholder="Operative findings" value={form.findings} onChange={(v) => setForm({ ...form, findings: v })} rows={3}/>
      <VoiceInput placeholder="Steps performed" value={form.steps} onChange={(v) => setForm({ ...form, steps: v })} rows={3}/>
      <VoiceInput placeholder="Complications (if any)" value={form.complications} onChange={(v) => setForm({ ...form, complications: v })} rows={2}/>
      <VoiceInput placeholder="Post-op orders" value={form.post_op_orders} onChange={(v) => setForm({ ...form, post_op_orders: v })} rows={2}/>
      <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.consent_taken} onChange={(e) => setForm({ ...form, consent_taken: e.target.checked })} className="accent-blue-700"/> Informed consent taken</label>
      <div className="border-t pt-2 space-y-2">
        {rows.map((p) => (
          <details key={p.id} className="border rounded-sm p-2" data-testid={`ipd-proc-row-${p.id}`}>
            <summary className="cursor-pointer flex items-center justify-between">
              <div>
                <div className="font-medium text-sm">{p.procedure}</div>
                <div className="text-[11px] text-slate-500">{p.surgeon} · {p.anaesthesia_type}{p.anaesthetist ? ` · ${p.anaesthetist}` : ""} · {new Date(p.created_at).toLocaleString("en-IN")}</div>
              </div>
              {!p.consent_taken && <Badge className="bg-red-100 text-red-800 rounded-sm">No consent</Badge>}
            </summary>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[12px]">
              {p.indication && <div><b>Indication:</b> {p.indication}</div>}
              {p.findings && <div><b>Findings:</b> {p.findings}</div>}
              {p.steps && <div className="col-span-2"><b>Steps:</b> {p.steps}</div>}
              {p.complications && <div className="col-span-2 text-red-700"><b>Complications:</b> {p.complications}</div>}
              {p.post_op_orders && <div className="col-span-2"><b>Post-op:</b> {p.post_op_orders}</div>}
            </div>
          </details>
        ))}
        {rows.length === 0 && <div className="p-6 text-center text-xs text-slate-500">No procedures documented yet.</div>}
      </div>
    </div>
  );
}
